import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  FileText,
  Clock,
  Printer,
  Eye,
  Paperclip,
  X,
  RotateCcw,
  Inbox,
  Send,
  FileCheck,
  Download,
  AlertCircle,
  Edit3,
  Trash2
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import SignaturePad from '../components/SignaturePad';
import Podpis from '../components/Podpis';
import PanelSaldaUrlopu from '../components/PanelSaldaUrlopu';
import { podpisHtml, LINK_CZCIONKI_PODPISU } from '../utils/podpis';
import toast from 'react-hot-toast';
import kadryService from '../services/kadryService';
import { formatSignerNameUnderPad } from '../utils/userDisplayName';

const MAIN_TABS = ['moje', 'do-podpisania', 'podpisane'];

function escapeHtmlPrint(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Saldo urlopu autora wniosku — okno decyzji przełożonego i szczegóły wniosku. */
const SaldoWnioskuUrlopowego = ({ wniosekId }) => {
  const { data, isLoading, isError } = useQuery(
    ['saldo-wniosku-urlopowego', wniosekId],
    () => kadryService.getSaldoWnioskuUrlopowego(wniosekId),
    { enabled: Boolean(wniosekId), staleTime: 30 * 1000, retry: 1 }
  );
  return <PanelSaldaUrlopu saldo={data?.saldo} tryb="decyzja" ladowanie={isLoading} blad={isError} />;
};

const Wnioski = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const tabFromUrl = searchParams.get('tab');
  const [mainTab, setMainTab] = useState(MAIN_TABS.includes(tabFromUrl || '') ? (tabFromUrl || 'moje') : 'moje');

  useEffect(() => {
    const t = searchParams.get('tab');
    let tab = MAIN_TABS.includes(t || '') ? (t || 'moje') : 'moje';
    // Jeśli tab to "do-podpisania" lub "podpisane" ale użytkownik nie może podpisywać - wróć na "moje"
    const możePod = user?.isPrzelozony || user?.department === 'HR' || user?.role === 'super admin';
    const widziPodpis = możePod || user?.department === 'KSIĘGOWOŚĆ';
    if (tab === 'do-podpisania' && !możePod) tab = 'moje';
    if (tab === 'podpisane' && !widziPodpis) {
      tab = 'moje';
      setSearchParams({});
    }
    setMainTab(tab);
  }, [searchParams, user?.isPrzelozony, user?.department, user?.role]);
  const [activeSubTab, setActiveSubTab] = useState('wszystkie');
  const [selectedWniosek, setSelectedWniosek] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [podpisPrzełożonego, setPodpisPrzełożonego] = useState('');
  const [signStatus, setSignStatus] = useState('zatwierdzony');
  const [powodOdrzucenia, setPowodOdrzucenia] = useState('');
  const [showZwrotModal, setShowZwrotModal] = useState(false);
  const [komentarzZwrotu, setKomentarzZwrotu] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [powodUsuniecia, setPowodUsuniecia] = useState('');
  const [cofnijujacyWniosekId, setCofnijujacyWniosekId] = useState(null);
  const [zalacznikLaduje, setZalacznikLaduje] = useState(null);

  const jestSuperAdmin = user?.role === 'super admin';
  const SUGESTIE_POWODU_USUNIECIA = [
    'Prośba pracownika',
    'Błędnie zatwierdzony wniosek',
    'Duplikat wniosku',
    'Korekta administracyjna'
  ];

  const podgladZalacznika = async (wniosekId) => {
    setZalacznikLaduje(wniosekId);
    try {
      const blob = await kadryService.fetchZalacznik(wniosekId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Nie udało się pobrać załącznika');
    } finally {
      setZalacznikLaduje(null);
    }
  };

  const setTab = (tab) => {
    setMainTab(tab);
    setSearchParams({ tab });
  };

  // Moje wnioski – klucz z userId, by uniknąć cache między użytkownikami
  const { data: mojeData, isLoading: isLoadingMoje } = useQuery(
    ['wnioski-urlopowe', user?.id || user?._id],
    kadryService.getWnioskiUrlopowe,
    { refetchInterval: 30000, enabled: !!user && mainTab === 'moje' }
  );

  // Zakładki "Do podpisania" i "Podpisane" – przełożony/HR/admin może podpisywać; Księgowość widzi tylko "Podpisane" (podgląd załączników)
  const możePodpisywać = user?.isPrzelozony || user?.department === 'HR' || user?.role === 'super admin';
  const widziPodpisane = możePodpisywać || user?.department === 'KSIĘGOWOŚĆ';

  // Do podpisania – zawsze pobieraj gdy użytkownik może podpisywać, żeby licznik w zakładce był poprawny
  const { data: doPodpisaniaData, isLoading: isLoadingDoPodpisania } = useQuery(
    'wnioski-do-podpisania',
    kadryService.getWnioskiDoPodpisania,
    { refetchInterval: 30000, enabled: !!user && możePodpisywać }
  );

  // Podpisane – zawsze pobieraj gdy użytkownik widzi podpisane, żeby licznik w zakładce był poprawny
  const { data: podpisaneData, isLoading: isLoadingPodpisane } = useQuery(
    'wnioski-podpisane',
    kadryService.getWnioskiPodpisane,
    { refetchInterval: 30000, enabled: !!user && widziPodpisane }
  );

  // Pracownik widzi TYLKO wnioski gdzie ON JEST PRACOWNIKIEM (imię+nazwisko na formularzu)
  const rawMoje = mojeData?.wnioski || [];
  const normalize = (s) => (s || '').toLowerCase().trim();
  const wnioskiMoje = rawMoje.filter(w => {
    const imieMatch = normalize(w.pracownikImie) === normalize(user?.firstName);
    const nazwiskoMatch = normalize(w.pracownikNazwisko) === normalize(user?.lastName);
    return imieMatch && nazwiskoMatch;
  });
  const wnioskiDoPodpisania = doPodpisaniaData?.wnioski || [];
  const wnioskiPodpisane = podpisaneData?.wnioski || [];

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pl-PL');
  };

  const getStatusBadge = (status, wniosek) => {
    if (wniosek?.oczekujeNaPoprawke) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Do poprawy
        </span>
      );
    }
    const badges = {
      oczekujacy: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock className="w-3 h-3 mr-1" />
        Oczekujący
      </span>,
      zatwierdzony: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Zatwierdzony
      </span>,
      odrzucony: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <XCircle className="w-3 h-3 mr-1" />
        Odrzucony
      </span>,
      cofniete: <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <RotateCcw className="w-3 h-3 mr-1" />
        Cofnięty
      </span>
    };
    return badges[status] || badges.oczekujacy;
  };

  const getRodzajUrlopuLabel = (rodzaj) => {
    const labels = {
      wypoczynkowy: 'Wypoczynkowy',
      okolicznosciowy: 'Okolicznościowy',
      bezplatny: 'Bezpłatny',
      na_zadanie: 'Na żądanie'
    };
    return labels[rodzaj] || rodzaj;
  };

  const getUrlopDaysModeLabel = (w) =>
    w?.wliczWeekendyWDniUrlopowe === true
      ? 'Dni kalendarzowe (weekendy i święta w okresie)'
      : 'Dni robocze (bez weekendów i świąt państwowych)';

  // Podpisywanie
  const podpiszWniosekMutation = useMutation(
    ({ wniosekId, podpisData }) => kadryService.podpiszWniosekUrlopowy(wniosekId, podpisData),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries('wnioski-do-podpisania');
        queryClient.invalidateQueries('wnioski-podpisane');
        await queryClient.refetchQueries('wnioski-podpisane');
        toast.success('Wniosek został podpisany pomyślnie');
        setShowSignModal(false);
        setSelectedWniosek(null);
        setPodpisPrzełożonego('');
        setSignStatus('zatwierdzony');
        setPowodOdrzucenia('');
        setTab('podpisane');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Wystąpił błąd podczas podpisywania wniosku');
      }
    }
  );

  const zwrotDoPracownikaMutation = useMutation(
    ({ wniosekId, komentarz }) => kadryService.zwrocUrlopDoPracownika(wniosekId, komentarz),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-do-podpisania');
        queryClient.invalidateQueries(['wnioski-urlopowe', user?.id || user?._id]);
        toast.success('Wniosek zwrócony do pracownika do poprawy');
        setShowZwrotModal(false);
        setSelectedWniosek(null);
        setKomentarzZwrotu('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Wystąpił błąd podczas zwracania wniosku');
      }
    }
  );

  const cofnijWniosekMutation = useMutation(
    (wniosekId) => kadryService.cofnijWniosekUrlopowy(wniosekId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-urlopowe');
        toast.success('Wniosek został cofnięty pomyślnie');
        setCofnijujacyWniosekId(null);
      },
      onError: (error) => {
        setCofnijujacyWniosekId(null);
        toast.error(error.response?.data?.message || 'Wystąpił błąd podczas cofania wniosku');
      },
      onSettled: () => setCofnijujacyWniosekId(null)
    }
  );

  const usunWniosekMutation = useMutation(
    ({ wniosekId, powod }) => kadryService.usunWniosekUrlopowy(wniosekId, powod),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('wnioski-podpisane');
        queryClient.invalidateQueries(['wnioski-urlopowe', user?.id || user?._id]);
        toast.success('Wniosek urlopowy został usunięty');
        setShowDeleteModal(false);
        setShowDetailsModal(false);
        setSelectedWniosek(null);
        setPowodUsuniecia('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Wystąpił błąd podczas usuwania wniosku');
      }
    }
  );

  const handlePrint = (wniosek) => {
    const printWindow = window.open('', '_blank');
    const opisRaw = wniosek.opisUrlopu && String(wniosek.opisUrlopu).trim() !== '' ? String(wniosek.opisUrlopu).trim() : '';
    printWindow.document.write(`
      <html>
        <head><title>Wniosek Urlopowy</title>${LINK_CZCIONKI_PODPISU}
          <style>
            body{font-family:Arial;padding:20px}
            .label{font-weight:bold;min-width:150px}
            .field{margin-bottom:15px}
          </style>
        </head>
        <body>
          <h1>WNIOSEK URLOPOWY</h1>
          <p><strong>Data złożenia:</strong> ${formatDate(wniosek.dataZlozenia || wniosek.createdAt)}</p>
          <div class="field"><span class="label">Pracownik:</span> ${escapeHtmlPrint(wniosek.pracownikImie)} ${escapeHtmlPrint(wniosek.pracownikNazwisko)}</div>
          ${wniosek.stanowisko ? `<div class="field"><span class="label">Stanowisko:</span> ${escapeHtmlPrint(wniosek.stanowisko)}</div>` : ''}
          <div class="field"><span class="label">Rodzaj urlopu:</span> ${getRodzajUrlopuLabel(wniosek.rodzajUrlopu)}</div>
          <div class="field"><span class="label">Okres:</span> ${formatDate(wniosek.dataOd)} - ${formatDate(wniosek.dataDo)}</div>
          <div class="field"><span class="label">Liczba dni:</span> ${wniosek.liczbaDni}</div>
          <div class="field"><span class="label">Sposób liczenia dni:</span> ${escapeHtmlPrint(getUrlopDaysModeLabel(wniosek))}</div>
          ${opisRaw ? `<div class="field"><span class="label">Opis urlopu:</span> <span style="white-space:pre-wrap">${escapeHtmlPrint(opisRaw)}</span></div>` : ''}
          ${wniosek.podpisPracownika ? `<p><strong>Podpis pracownika:</strong></p>${podpisHtml(wniosek.podpisPracownika, { maxWidth: 200, maxHeight: 80, align: 'left' })}<p style="margin-top:6px;font-size:14px">${escapeHtmlPrint(wniosek.pracownikImie)} ${escapeHtmlPrint(wniosek.pracownikNazwisko)}</p>` : ''}
          ${wniosek.podpisPrzełożonego ? `<p><strong>Podpis przełożonego:</strong></p>${podpisHtml(wniosek.podpisPrzełożonego, { maxWidth: 200, maxHeight: 80, align: 'left' })}${wniosek.podpisPrzełożonegoImieNazwisko ? `<p style="margin-top:6px;font-size:14px">${escapeHtmlPrint(wniosek.podpisPrzełożonegoImieNazwisko)}</p>` : ''}` : ''}
          ${wniosek.status !== 'oczekujacy' ? `<p><strong>Status:</strong> ${wniosek.status === 'zatwierdzony' ? 'Zatwierdzony' : 'Odrzucony'}</p>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleDownloadPdf = async (wniosek) => {
    try {
      const miejscowosc = process.env.REACT_APP_MIEJSCOWOSC || 'Warszawa';
      const dataZlozenia = formatDate(wniosek.dataZlozenia || wniosek.createdAt);
      const dataOd = formatDate(wniosek.dataOd);
      const dataDo = formatDate(wniosek.dataDo);
      const statusLabel = wniosek.status === 'zatwierdzony' ? 'Zatwierdzony' : wniosek.status === 'odrzucony' ? 'Odrzucony' : 'Cofnięty';
      const rodzajDopelniacz = { wypoczynkowy: 'wypoczynkowego', okolicznosciowy: 'okolicznościowego', bezplatny: 'bezpłatnego', na_zadanie: 'na żądanie' }[wniosek.rodzajUrlopu] || 'wypoczynkowego';
      const opisRaw = wniosek.opisUrlopu && String(wniosek.opisUrlopu).trim() !== '' ? String(wniosek.opisUrlopu).trim() : '';

      const htmlContent = `
        <div id="pdf-wniosek-template" style="
          font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
          width: 794px;
          padding: 50px 60px 50px 60px;
          box-sizing: border-box;
          background: #ffffff;
          color: #000000;
          font-size: 13px;
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
        ">
          <div style="text-align: right; margin-bottom: 30px;">
            <p style="margin: 0; font-size: 12px; color: #333;">${miejscowosc}, ${dataZlozenia}</p>
          </div>
          <div style="margin-bottom: 24px;">
            <p style="margin: 0 0 4px 0; font-size: 13px;"><strong>Dane pracownika:</strong></p>
            <p style="margin: 0; font-size: 13px;">${escapeHtmlPrint(wniosek.pracownikImie || '')} ${escapeHtmlPrint(wniosek.pracownikNazwisko || '')}${wniosek.stanowisko ? ` – ${escapeHtmlPrint(wniosek.stanowisko)}` : ''}</p>
          </div>
          <h1 style="
            text-align: center;
            font-size: 18px;
            font-weight: 700;
            margin: 0 0 28px 0;
          ">Wniosek o udzielenie urlopu ${rodzajDopelniacz}</h1>
          <p style="margin: 0 0 24px 0; font-size: 13px; text-align: justify;">
            Zwracam się z uprzejmą prośbą o udzielenie urlopu ${rodzajDopelniacz} w wymiarze <strong>${wniosek.liczbaDni} dni</strong> w terminie od <strong>${dataOd}</strong> do <strong>${dataDo}</strong>.
          </p>
          <p style="margin: -16px 0 24px 0; font-size: 11px; color: #444;">
            Sposób liczenia dni: ${escapeHtmlPrint(getUrlopDaysModeLabel(wniosek))}
          </p>
          ${opisRaw ? `
          <p style="margin: 0 0 20px 0; font-size: 12px; color: #333; white-space: pre-wrap;">
            <strong>Opis urlopu:</strong> ${escapeHtmlPrint(opisRaw)}
          </p>
          ` : ''}
          ${wniosek.status !== 'oczekujacy' ? `
          <p style="margin: 0 0 28px 0; font-size: 13px; text-align: center;">
            Wyrażam zgodę na udzielenie urlopu wypoczynkowego w wymiarze i terminie wskazanym we wniosku.
          </p>
          <p style="margin: 0 0 8px 0; font-size: 12px; color: #555;">Status: ${statusLabel}${wniosek.dataZatwierdzenia ? ` – Data rozpatrzenia: ${formatDate(wniosek.dataZatwierdzenia)}` : ''}</p>
          ` : ''}
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 50px; gap: 40px;">
            <div style="flex: 1;">
              ${wniosek.podpisPracownika ? `
              <p style="font-weight: 700; margin: 0 0 8px 0; font-size: 12px;">Podpis pracownika:</p>
              ${podpisHtml(wniosek.podpisPracownika, { maxWidth: 140, maxHeight: 55, fontSize: 22, align: 'left' })}
              <p style="margin: 6px 0 0 0; font-size: 11px;">${escapeHtmlPrint(wniosek.pracownikImie || '')} ${escapeHtmlPrint(wniosek.pracownikNazwisko || '')}</p>
              ` : '<p style="font-weight: 700; margin: 0; font-size: 12px;">Podpis pracownika:</p><div style="border-bottom: 1px dotted #999; width: 140px; height: 40px; margin-top: 4px;"></div>'}
            </div>
            <div style="flex: 1; display: flex; flex-direction: column; align-items: flex-end;">
              ${wniosek.podpisPrzełożonego ? `
              <p style="font-weight: 700; margin: 0 0 8px 0; font-size: 12px;">Podpis przełożonego:</p>
              ${podpisHtml(wniosek.podpisPrzełożonego, { maxWidth: 140, maxHeight: 55, fontSize: 22, align: 'right' })}
              ${wniosek.podpisPrzełożonegoImieNazwisko ? `<p style="margin: 6px 0 0 0; font-size: 11px; text-align: right;">${escapeHtmlPrint(wniosek.podpisPrzełożonegoImieNazwisko)}</p>` : ''}
              ` : '<p style="font-weight: 700; margin: 0; font-size: 12px;">Podpis przełożonego:</p><div style="border-bottom: 1px dotted #999; width: 140px; height: 40px; margin-top: 4px;"></div>'}
            </div>
          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.style.cssText = 'position: fixed; left: -9999px; top: 0; z-index: -1;';
      container.innerHTML = htmlContent;
      document.body.appendChild(container);

      const templateEl = container.querySelector('#pdf-wniosek-template');
      await new Promise(resolve => setTimeout(resolve, 100));
      const canvas = await html2canvas(templateEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      document.body.removeChild(container);

      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidth = 210;
      const pageHeight = 297;
      const imgWidth = pageWidth;
      const imgHeight = Math.min((canvas.height * pageWidth) / canvas.width, pageHeight);
      doc.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      const fileName = `wniosek-urlopowy-${(wniosek.pracownikNazwisko || 'wniosek')}-${formatDate(wniosek.dataOd)}.pdf`.replace(/\s/g, '-');
      doc.save(fileName);
      toast.success('PDF został pobrany');
    } catch (err) {
      console.error('Błąd generowania PDF:', err);
      toast.error('Nie udało się wygenerować PDF');
    }
  };

  // Filtrowanie moich wniosków
  const wnioskiOczekujace = wnioskiMoje.filter(w => w.status === 'oczekujacy');
  const wnioskiZatwierdzone = wnioskiMoje.filter(w => w.status === 'zatwierdzony');
  const wnioskiOdrzucone = wnioskiMoje.filter(w => w.status === 'odrzucony');
  const wnioskiCofniete = wnioskiMoje.filter(w => w.status === 'cofniete');
  const filteredMoje = activeSubTab === 'wszystkie' ? wnioskiMoje
    : activeSubTab === 'oczekujace' ? wnioskiOczekujace
    : activeSubTab === 'zatwierdzone' ? wnioskiZatwierdzone
    : activeSubTab === 'odrzucone' ? wnioskiOdrzucone
    : activeSubTab === 'cofniete' ? wnioskiCofniete
    : wnioskiMoje;

  const isLoading = mainTab === 'moje' ? isLoadingMoje
    : mainTab === 'do-podpisania' ? isLoadingDoPodpisania
    : isLoadingPodpisane;

  const renderMojeWnioskiCard = (wniosek) => (
    <div key={wniosek._id} className="bg-white shadow rounded-lg p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-4 gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                {wniosek.pracownikImie} {wniosek.pracownikNazwisko}
              </h3>
              {wniosek.stanowisko && <p className="text-xs sm:text-sm text-gray-500">{wniosek.stanowisko}</p>}
            </div>
            {getStatusBadge(wniosek.status, wniosek)}
          </div>
          {wniosek.oczekujeNaPoprawke && wniosek.aktywnyKomentarzZwrotu?.komentarz && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm font-medium text-orange-900 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                Wniosek zwrócony do poprawy
              </p>
              <p className="text-xs text-orange-800 mt-1">
                {wniosek.aktywnyKomentarzZwrotu.zwrocilImieNazwisko || 'Przełożony'} (
                {formatDate(wniosek.aktywnyKomentarzZwrotu.data)}):
              </p>
              <p className="text-sm text-orange-900 mt-1 whitespace-pre-wrap">{wniosek.aktywnyKomentarzZwrotu.komentarz}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div><p className="text-xs text-gray-500">Rodzaj urlopu</p><p className="text-sm font-medium">{getRodzajUrlopuLabel(wniosek.rodzajUrlopu)}</p></div>
            <div><p className="text-xs text-gray-500">Okres</p><p className="text-sm font-medium">{formatDate(wniosek.dataOd)} - {formatDate(wniosek.dataDo)}</p></div>
            <div><p className="text-xs text-gray-500">Liczba dni</p><p className="text-sm font-medium">{wniosek.liczbaDni} {wniosek.liczbaDni === 1 ? 'dzień' : 'dni'}</p></div>
            <div className="sm:col-span-3"><p className="text-xs text-gray-500">Sposób liczenia</p><p className="text-sm text-gray-700">{getUrlopDaysModeLabel(wniosek)}</p></div>
          </div>
          {wniosek.opisUrlopu && String(wniosek.opisUrlopu).trim() !== '' && (
            <div className="mb-4">
              <p className="text-xs text-gray-500">Opis urlopu</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{String(wniosek.opisUrlopu).trim()}</p>
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-1 text-xs text-gray-500">
            <p>Data złożenia: {formatDate(wniosek.createdAt)}</p>
            {wniosek.przełożony && <p>Przełożony: {wniosek.przełożony.firstName} {wniosek.przełożony.lastName}</p>}
            {wniosek.dataZatwierdzenia && <p>Data rozpatrzenia: {formatDate(wniosek.dataZatwierdzenia)}</p>}
            {wniosek.status === 'odrzucony' && wniosek.powodOdrzucenia && (
              <p className="text-red-700 whitespace-pre-wrap">Powód odrzucenia: {wniosek.powodOdrzucenia}</p>
            )}
          </div>
        </div>
        <div className="flex flex-row sm:flex-col space-x-2 sm:space-x-0 sm:space-y-2 sm:flex-shrink-0 flex-wrap gap-2">
          {wniosek.oczekujeNaPoprawke && (
            <Link to={`/kadry/dokumenty/wniosek-urlopowy/${wniosek._id}`} className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center space-x-2 text-sm">
              <Edit3 className="h-4 w-4" /><span>Popraw wniosek</span>
            </Link>
          )}
          <button onClick={() => { setSelectedWniosek(wniosek); setShowDetailsModal(true); }} className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm">
            <Eye className="h-4 w-4" /><span>Szczegóły</span>
          </button>
          {wniosek.załącznikUrl && (
            <button onClick={() => podgladZalacznika(wniosek._id)} disabled={zalacznikLaduje === wniosek._id} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm disabled:opacity-50">
              <Paperclip className="h-4 w-4" />
              <span>{zalacznikLaduje === wniosek._id ? 'Otwieranie...' : 'Podgląd załącznika'}</span>
            </button>
          )}
          <button onClick={() => handlePrint(wniosek)} className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm">
            <Printer className="h-4 w-4" /><span>Drukuj</span>
          </button>
          {(wniosek.status === 'zatwierdzony' || wniosek.status === 'odrzucony') && (
            <button onClick={() => handleDownloadPdf(wniosek)} className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center space-x-2 text-sm">
              <Download className="h-4 w-4" /><span>Pobierz PDF</span>
            </button>
          )}
          {wniosek.status === 'oczekujacy' && !wniosek.podpisPrzełożonego && !wniosek.oczekujeNaPoprawke && (
            <button onClick={() => { if (window.confirm('Czy na pewno chcesz cofnąć ten wniosek?')) { setCofnijujacyWniosekId(wniosek._id); cofnijWniosekMutation.mutate(wniosek._id); } }}
              disabled={cofnijujacyWniosekId === wniosek._id} className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-2 text-sm">
              <RotateCcw className="h-4 w-4" /><span>{cofnijujacyWniosekId === wniosek._id ? 'Cofanie...' : 'Cofnij'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderPrzelozonyWniosekCard = (wniosek, canSign = false) => (
    <div key={wniosek._id} className="bg-white shadow rounded-lg p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mb-4 gap-2">
            <div><h3 className="text-base font-semibold text-gray-900">{wniosek.pracownikImie} {wniosek.pracownikNazwisko}</h3>{wniosek.stanowisko && <p className="text-xs text-gray-500">{wniosek.stanowisko}</p>}</div>
            {getStatusBadge(wniosek.status, wniosek)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div><p className="text-xs text-gray-500">Rodzaj urlopu</p><p className="text-sm font-medium">{getRodzajUrlopuLabel(wniosek.rodzajUrlopu)}</p></div>
            <div><p className="text-xs text-gray-500">Okres</p><p className="text-sm font-medium">{formatDate(wniosek.dataOd)} - {formatDate(wniosek.dataDo)}</p></div>
            <div><p className="text-xs text-gray-500">Liczba dni</p><p className="text-sm font-medium">{wniosek.liczbaDni}</p></div>
            <div className="sm:col-span-3"><p className="text-xs text-gray-500">Sposób liczenia</p><p className="text-sm text-gray-700">{getUrlopDaysModeLabel(wniosek)}</p></div>
          </div>
          {wniosek.opisUrlopu && String(wniosek.opisUrlopu).trim() !== '' && (
            <div className="mb-4">
              <p className="text-xs text-gray-500">Opis urlopu</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{String(wniosek.opisUrlopu).trim()}</p>
            </div>
          )}
          <div className="text-xs text-gray-500 space-y-1">
            {wniosek.createdBy && <p>Złożony przez: {wniosek.createdBy.firstName && wniosek.createdBy.lastName ? `${wniosek.createdBy.firstName} ${wniosek.createdBy.lastName}` : (wniosek.createdBy.username || '-')}</p>}
            <p>Data złożenia: {formatDate(wniosek.createdAt)}</p>
            {wniosek.dataZatwierdzenia && <p>Data rozpatrzenia: {formatDate(wniosek.dataZatwierdzenia)}</p>}
            {wniosek.status === 'odrzucony' && wniosek.powodOdrzucenia && (
              <p className="text-red-700 whitespace-pre-wrap">Powód odrzucenia: {wniosek.powodOdrzucenia}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:flex-shrink-0 flex-wrap">
          <button onClick={() => { setSelectedWniosek(wniosek); setShowDetailsModal(true); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm">
            <Eye className="h-4 w-4" /><span>Szczegóły</span>
          </button>
          {wniosek.załącznikUrl && (
            <button onClick={() => podgladZalacznika(wniosek._id)} disabled={zalacznikLaduje === wniosek._id} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 text-sm disabled:opacity-50">
              <Paperclip className="h-4 w-4" /><span>{zalacznikLaduje === wniosek._id ? 'Otwieranie...' : 'Podgląd załącznika'}</span>
            </button>
          )}
          {!canSign && (wniosek.status === 'zatwierdzony' || wniosek.status === 'odrzucony') && (
            <button onClick={() => handleDownloadPdf(wniosek)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center space-x-2 text-sm">
              <Download className="h-4 w-4" /><span>Pobierz PDF</span>
            </button>
          )}
          {jestSuperAdmin && !canSign && (wniosek.status === 'zatwierdzony' || wniosek.status === 'odrzucony') && (
            <button
              onClick={() => { setSelectedWniosek(wniosek); setPowodUsuniecia(''); setShowDeleteModal(true); }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2 text-sm"
              title="Usuń podpisany wniosek"
            >
              <Trash2 className="h-4 w-4" /><span>Usuń</span>
            </button>
          )}
          {canSign && (
            <>
              <button
                onClick={() => { setSelectedWniosek(wniosek); setShowZwrotModal(true); setKomentarzZwrotu(''); }}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center space-x-2 text-sm"
              >
                <RotateCcw className="h-4 w-4" /><span>Zwróć do poprawy</span>
              </button>
              <button onClick={() => { setSelectedWniosek(wniosek); setShowSignModal(true); setPodpisPrzełożonego(''); setSignStatus('zatwierdzony'); setPowodOdrzucenia(''); }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm">
                <FileText className="h-4 w-4" /><span>Podpisz wniosek</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const emptyMessage = () => {
    if (mainTab === 'moje') return wnioskiMoje.length === 0 ? 'Nie masz jeszcze żadnych wniosków urlopowych.' : `Brak wniosków w kategorii "${activeSubTab}".`;
    if (mainTab === 'do-podpisania') return 'Nie masz wniosków oczekujących na podpis.';
    return 'Nie podpisałeś jeszcze żadnych wniosków.';
  };

  const currentList = mainTab === 'moje' ? filteredMoje : mainTab === 'do-podpisania' ? wnioskiDoPodpisania : wnioskiPodpisane;

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      <div className="bg-white shadow rounded-lg p-4 sm:p-6 print:hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Link to="/kadry" className="p-2 rounded-lg hover:bg-gray-100 transition-colors self-start">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          {mainTab === 'moje' && (
            <Link to="/kadry/dokumenty/wniosek-urlopowy" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 text-sm">
              <FileText className="h-4 w-4" /><span>Nowy wniosek</span>
            </Link>
          )}
        </div>
      </div>

      {/* Główne zakładki */}
      <div className="bg-white shadow rounded-lg p-2 sm:p-4 print:hidden">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
          <button onClick={() => setTab('moje')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 ${mainTab === 'moje' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            <Inbox className="h-4 w-4" /><span>Moje wnioski ({wnioskiMoje.length})</span>
          </button>
          {możePodpisywać && (
            <button onClick={() => setTab('do-podpisania')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 ${mainTab === 'do-podpisania' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Send className="h-4 w-4" /><span>Do podpisania ({wnioskiDoPodpisania.length})</span>
            </button>
          )}
          {widziPodpisane && (
            <button onClick={() => setTab('podpisane')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 ${mainTab === 'podpisane' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <FileCheck className="h-4 w-4" /><span>Podpisane ({wnioskiPodpisane.length})</span>
            </button>
          )}
        </div>

        {/* Sub-zakładki tylko dla "Moje wnioski" */}
        {mainTab === 'moje' && wnioskiMoje.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
            {[
              ['wszystkie', 'Wszystkie', wnioskiMoje.length],
              ['oczekujace', 'Oczekujące', wnioskiOczekujace.length],
              ['zatwierdzone', 'Zatwierdzone', wnioskiZatwierdzone.length],
              ['odrzucone', 'Odrzucone', wnioskiOdrzucone.length],
              ['cofniete', 'Cofnięte', wnioskiCofniete.length]
            ].map(([key, label, count]) => (
              <button key={key} onClick={() => setActiveSubTab(key)} className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${activeSubTab === key ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {label} ({count})
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Statystyki (tylko Moje wnioski) */}
      {mainTab === 'moje' && wnioskiMoje.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 print:hidden">
          <div className="bg-white shadow rounded-lg p-4 flex items-center justify-between">
            <div><p className="text-xs text-gray-500">Oczekujące</p><p className="text-xl font-bold text-yellow-600">{wnioskiOczekujace.length}</p></div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
          <div className="bg-white shadow rounded-lg p-4 flex items-center justify-between">
            <div><p className="text-xs text-gray-500">Zatwierdzone</p><p className="text-xl font-bold text-green-600">{wnioskiZatwierdzone.length}</p></div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <div className="bg-white shadow rounded-lg p-4 flex items-center justify-between">
            <div><p className="text-xs text-gray-500">Odrzucone</p><p className="text-xl font-bold text-red-600">{wnioskiOdrzucone.length}</p></div>
            <XCircle className="h-8 w-8 text-red-500" />
          </div>
          <div className="bg-white shadow rounded-lg p-4 flex items-center justify-between">
            <div><p className="text-xs text-gray-500">Cofnięte</p><p className="text-xl font-bold text-gray-600">{wnioskiCofniete.length}</p></div>
            <RotateCcw className="h-8 w-8 text-gray-500" />
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-12"><LoadingSpinner /></div>
      ) : currentList.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{mainTab === 'moje' ? 'Brak wniosków' : mainTab === 'do-podpisania' ? 'Brak wniosków do podpisania' : 'Brak podpisanych wniosków'}</h2>
          <p className="text-gray-500 mb-6">{emptyMessage()}</p>
          {mainTab === 'moje' && wnioskiMoje.length === 0 && (
            <Link to="/kadry/dokumenty/wniosek-urlopowy" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <FileText className="h-4 w-4 mr-2" />Złóż pierwszy wniosek
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {mainTab === 'moje' && filteredMoje.map(renderMojeWnioskiCard)}
          {mainTab === 'do-podpisania' && wnioskiDoPodpisania.map(w => renderPrzelozonyWniosekCard(w, true))}
          {mainTab === 'podpisane' && wnioskiPodpisane.map(w => renderPrzelozonyWniosekCard(w, false))}
        </div>
      )}

      {/* Modal szczegółów */}
      {showDetailsModal && selectedWniosek && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Szczegóły wniosku</h3>
              <button onClick={() => { setShowDetailsModal(false); setSelectedWniosek(null); }} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Pracownik</p><p className="font-medium">{selectedWniosek.pracownikImie} {selectedWniosek.pracownikNazwisko}</p></div>
                <div><p className="text-sm text-gray-500">Rodzaj urlopu</p><p className="font-medium">{getRodzajUrlopuLabel(selectedWniosek.rodzajUrlopu)}</p></div>
                <div><p className="text-sm text-gray-500">Status</p><div>{getStatusBadge(selectedWniosek.status, selectedWniosek)}</div></div>
                <div><p className="text-sm text-gray-500">Okres</p><p className="font-medium">{formatDate(selectedWniosek.dataOd)} - {formatDate(selectedWniosek.dataDo)}</p></div>
                <div><p className="text-sm text-gray-500">Liczba dni</p><p className="font-medium">{selectedWniosek.liczbaDni}</p></div>
                <div className="col-span-2"><p className="text-sm text-gray-500">Sposób liczenia dni</p><p className="font-medium text-gray-800">{getUrlopDaysModeLabel(selectedWniosek)}</p></div>
                <div><p className="text-sm text-gray-500">Data złożenia</p><p className="font-medium">{formatDate(selectedWniosek.createdAt)}</p></div>
              </div>
              {selectedWniosek.status === 'oczekujacy' && (
                <SaldoWnioskuUrlopowego wniosekId={selectedWniosek._id} />
              )}
              {selectedWniosek.opisUrlopu && String(selectedWniosek.opisUrlopu).trim() !== '' && (
                <div>
                  <p className="text-sm text-gray-500">Opis urlopu</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap mt-1">{String(selectedWniosek.opisUrlopu).trim()}</p>
                </div>
              )}
              {selectedWniosek.oczekujeNaPoprawke && selectedWniosek.aktywnyKomentarzZwrotu?.komentarz && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-medium text-orange-900">Uwagi przełożonego (do poprawy)</p>
                  <p className="text-sm text-orange-900 mt-1 whitespace-pre-wrap">{selectedWniosek.aktywnyKomentarzZwrotu.komentarz}</p>
                </div>
              )}
              {selectedWniosek.status === 'odrzucony' && selectedWniosek.powodOdrzucenia && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-900">Powód odrzucenia</p>
                  <p className="text-sm text-red-900 mt-1 whitespace-pre-wrap">{selectedWniosek.powodOdrzucenia}</p>
                </div>
              )}
              {selectedWniosek.podpisPracownika && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Podpis pracownika</p>
                  <Podpis value={selectedWniosek.podpisPracownika} alt="Podpis" className="max-w-xs" />
                  <p className="mt-2 text-sm font-medium text-gray-900">
                    {selectedWniosek.pracownikImie} {selectedWniosek.pracownikNazwisko}
                  </p>
                </div>
              )}
              {selectedWniosek.podpisPrzełożonego && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Podpis przełożonego</p>
                  <Podpis value={selectedWniosek.podpisPrzełożonego} alt="Podpis" className="max-w-xs" />
                  {selectedWniosek.podpisPrzełożonegoImieNazwisko && (
                    <p className="mt-2 text-sm font-medium text-gray-900">{selectedWniosek.podpisPrzełożonegoImieNazwisko}</p>
                  )}
                </div>
              )}
              {selectedWniosek.załącznikUrl && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Załącznik</p>
                  <button onClick={() => podgladZalacznika(selectedWniosek._id)} disabled={zalacznikLaduje === selectedWniosek._id} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50">
                    <Paperclip className="h-4 w-4 mr-2" />
                    {zalacznikLaduje === selectedWniosek._id ? 'Otwieranie...' : (selectedWniosek.załącznikNazwa || 'Podgląd załącznika')}
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t flex-wrap">
              <button onClick={() => { setShowDetailsModal(false); setSelectedWniosek(null); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Zamknij</button>
              <button onClick={() => handlePrint(selectedWniosek)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"><Printer className="h-4 w-4" />Drukuj</button>
              {(selectedWniosek.status === 'zatwierdzony' || selectedWniosek.status === 'odrzucony') && (
                <button onClick={() => handleDownloadPdf(selectedWniosek)} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center space-x-2"><Download className="h-4 w-4" />Pobierz PDF</button>
              )}
              {jestSuperAdmin && (selectedWniosek.status === 'zatwierdzony' || selectedWniosek.status === 'odrzucony') && selectedWniosek.podpisPrzełożonego && (
                <button
                  onClick={() => { setShowDetailsModal(false); setPowodUsuniecia(''); setShowDeleteModal(true); }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center space-x-2"
                >
                  <Trash2 className="h-4 w-4" />Usuń wniosek
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal podpisywania */}
      {showSignModal && selectedWniosek && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Podpisz wniosek urlopowy</h3>
              <button onClick={() => { setShowSignModal(false); setSelectedWniosek(null); setPodpisPrzełożonego(''); setPowodOdrzucenia(''); }} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold mb-2">Szczegóły wniosku</h4>
              <p><span className="text-gray-500">Pracownik:</span> {selectedWniosek.pracownikImie} {selectedWniosek.pracownikNazwisko}</p>
              <p><span className="text-gray-500">Rodzaj urlopu:</span> {getRodzajUrlopuLabel(selectedWniosek.rodzajUrlopu)}</p>
              <p><span className="text-gray-500">Okres:</span> {formatDate(selectedWniosek.dataOd)} - {formatDate(selectedWniosek.dataDo)} | Liczba dni: {selectedWniosek.liczbaDni}</p>
              <p className="text-sm"><span className="text-gray-500">Sposób liczenia:</span> {getUrlopDaysModeLabel(selectedWniosek)}</p>
              {selectedWniosek.opisUrlopu && String(selectedWniosek.opisUrlopu).trim() !== '' && (
                <p className="mt-2 text-sm whitespace-pre-wrap"><span className="text-gray-500">Opis urlopu:</span> {String(selectedWniosek.opisUrlopu).trim()}</p>
              )}
              {selectedWniosek.załącznikUrl && (
                <div className="mt-3">
                  <button onClick={() => podgladZalacznika(selectedWniosek._id)} disabled={zalacznikLaduje === selectedWniosek._id} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm disabled:opacity-50">
                    <Paperclip className="h-4 w-4 mr-2" />
                    {zalacznikLaduje === selectedWniosek._id ? 'Otwieranie...' : 'Podgląd załącznika'}
                  </button>
                </div>
              )}
            </div>
            <div className="mb-6">
              <SaldoWnioskuUrlopowego wniosekId={selectedWniosek._id} />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Status wniosku</label>
              <div className="flex space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="radio" name="status" value="zatwierdzony" checked={signStatus === 'zatwierdzony'} onChange={(e) => setSignStatus(e.target.value)} className="h-4 w-4 text-green-600" />
                  <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-1 text-green-600" />Zatwierdź</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="radio" name="status" value="odrzucony" checked={signStatus === 'odrzucony'} onChange={(e) => setSignStatus(e.target.value)} className="h-4 w-4 text-red-600" />
                  <span className="flex items-center"><XCircle className="h-4 w-4 mr-1 text-red-600" />Odrzuć</span>
                </label>
              </div>
            </div>
            {signStatus === 'odrzucony' && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Powód odrzucenia *</label>
                <textarea
                  value={powodOdrzucenia}
                  onChange={(e) => setPowodOdrzucenia(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Opisz powód odrzucenia wniosku..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <p className="mt-1 text-xs text-gray-500 text-right">{powodOdrzucenia.length}/2000</p>
              </div>
            )}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Podpis przełożonego *</label>
              <SignaturePad
                onSignatureChange={setPodpisPrzełożonego}
                width={400}
                height={150}
                signature={podpisPrzełożonego || undefined}
                signerName={formatSignerNameUnderPad(podpisPrzełożonego, undefined, user)}
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button onClick={() => { setShowSignModal(false); setSelectedWniosek(null); setPodpisPrzełożonego(''); setPowodOdrzucenia(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
              <button
                onClick={() => {
                  if (!podpisPrzełożonego) return;
                  if (signStatus === 'odrzucony' && !powodOdrzucenia.trim()) {
                    toast.error('Podaj powód odrzucenia');
                    return;
                  }
                  podpiszWniosekMutation.mutate({
                    wniosekId: selectedWniosek._id,
                    podpisData: {
                      podpisPrzełożonego,
                      status: signStatus,
                      ...(signStatus === 'odrzucony' ? { powodOdrzucenia: powodOdrzucenia.trim() } : {})
                    }
                  });
                }}
                disabled={!podpisPrzełożonego || podpiszWniosekMutation.isLoading || (signStatus === 'odrzucony' && !powodOdrzucenia.trim())}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {podpiszWniosekMutation.isLoading ? <LoadingSpinner size="sm" /> : <CheckCircle className="h-4 w-4" />}
                <span>Podpisz wniosek</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal zwrotu do poprawy */}
      {showZwrotModal && selectedWniosek && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Zwróć wniosek do poprawy</h3>
              <button onClick={() => { setShowZwrotModal(false); setSelectedWniosek(null); setKomentarzZwrotu(''); }} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Wniosek wróci do pracownika <strong>{selectedWniosek.pracownikImie} {selectedWniosek.pracownikNazwisko}</strong> z Twoimi uwagami. Po poprawce trafi ponownie do Ciebie do podpisania.
            </p>
            <label className="block text-sm font-medium mb-2">Komentarz dla pracownika *</label>
            <textarea
              value={komentarzZwrotu}
              onChange={(e) => setKomentarzZwrotu(e.target.value)}
              rows={5}
              maxLength={2000}
              placeholder="Opisz, co pracownik powinien poprawić..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p className="mt-1 text-xs text-gray-500 text-right">{komentarzZwrotu.length}/2000</p>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => { setShowZwrotModal(false); setSelectedWniosek(null); setKomentarzZwrotu(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Anuluj</button>
              <button
                onClick={() => zwrotDoPracownikaMutation.mutate({ wniosekId: selectedWniosek._id, komentarz: komentarzZwrotu.trim() })}
                disabled={!komentarzZwrotu.trim() || zwrotDoPracownikaMutation.isLoading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {zwrotDoPracownikaMutation.isLoading ? <LoadingSpinner size="sm" /> : <RotateCcw className="h-4 w-4" />}
                <span>Zwróć do pracownika</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal usuwania podpisanego wniosku (super admin) */}
      {showDeleteModal && selectedWniosek && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Usuń podpisany wniosek</h3>
              <button
                onClick={() => { setShowDeleteModal(false); setPowodUsuniecia(''); if (!showDetailsModal) setSelectedWniosek(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Usuwasz wniosek urlopowy pracownika{' '}
              <strong>{selectedWniosek.pracownikImie} {selectedWniosek.pracownikNazwisko}</strong>
              {' '}({formatDate(selectedWniosek.dataOd)} – {formatDate(selectedWniosek.dataDo)}, {selectedWniosek.liczbaDni} dni).
              Wniosek zniknie z listy podpisanych i nie będzie wliczany do bilansu urlopu.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {SUGESTIE_POWODU_USUNIECIA.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setPowodUsuniecia(s)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    powodUsuniecia === s
                      ? 'bg-red-50 border-red-300 text-red-800'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <label className="block text-sm font-medium mb-2">Powód usunięcia *</label>
            <textarea
              value={powodUsuniecia}
              onChange={(e) => setPowodUsuniecia(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Np. Prośba pracownika..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            <p className="mt-1 text-xs text-gray-500 text-right">{powodUsuniecia.length}/2000</p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => { setShowDeleteModal(false); setPowodUsuniecia(''); if (!showDetailsModal) setSelectedWniosek(null); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Anuluj
              </button>
              <button
                onClick={() => usunWniosekMutation.mutate({ wniosekId: selectedWniosek._id, powod: powodUsuniecia.trim() })}
                disabled={!powodUsuniecia.trim() || usunWniosekMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {usunWniosekMutation.isLoading ? <LoadingSpinner size="sm" /> : <Trash2 className="h-4 w-4" />}
                <span>Usuń wniosek</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wnioski;
